import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw } from 'lucide-react';
import useAuth from '../contexts/useAuth';
import { dynamoDBService, DocumentStats } from '../utils/dynamodb';
import { Layout } from '../components/Layout';
import { DocumentStatsTable } from '../components/DocumentStatsTable';

export const DocumentStatsPage: React.FC = () => {
  const { getCredentials } = useAuth();
  const [stats, setStats] = useState<DocumentStats[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const loadStats = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const credentials = await getCredentials();
      await dynamoDBService.initializeClient(credentials);
      const data = await dynamoDBService.getDocumentStats();
      setStats(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load document statistics');
    } finally {
      setIsLoading(false);
    }
  }, [getCredentials]);

  const handleExportCSV = () => {
    if (stats.length === 0) {
      alert('No data to export');
      return;
    }

    // Create CSV content
    const csvHeaders = [
      'Document URI',
      'Title',
      'Agent Source',
      'Citation Count',
      'Last Updated',
    ];

    const csvRows = stats.map((stat) => [
      `"${stat.document_uri}"`,
      `"${stat.title || 'N/A'}"`,
      `"${stat.agent_source || 'Unknown'}"`,
      stat.count.toString(),
      `"${stat.last_updated}"`,
    ]);

    const csvContent = [
      csvHeaders.join(','),
      ...csvRows.map((row) => row.join(',')),
    ].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `document-stats-${new Date().toISOString().split('T')[0]}.csv`,
    );
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const RefreshButton = (
    <button
      onClick={loadStats}
      disabled={isLoading}
      className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
    >
      <RefreshCw
        className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`}
      />
      Refresh
    </button>
  );

  return (
    <Layout sidebar={true}>
      <div className="flex flex-col h-full overflow-hidden px-4 sm:px-6 lg:px-8">
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4 flex justify-between items-start flex-shrink-0">
            <p className="text-sm text-red-800">{error}</p>
            <button
              onClick={() => setError(null)}
              className="text-red-600 hover:text-red-800"
            >
              ×
            </button>
          </div>
        )}
        <div className="flex-1 min-h-0">
          <DocumentStatsTable
            stats={stats}
            isLoading={isLoading}
            onExportCSV={handleExportCSV}
            RefreshButton={RefreshButton}
          />
        </div>
      </div>
    </Layout>
  );
};
